<template>
  <header class="pycad-topbar">
    <div class="pycad-topbar__scroll">
      <!-- Brand. Hidden when the app runs embedded in a host iframe. -->
      <template v-if="!isEmbedded">
        <div class="pycad-topbar__brand">
          <img
            :src="smileSuiteLogo"
            alt="SmileSuite"
            class="pycad-topbar__logo"
            :class="{ 'pycad-topbar__logo--mobile': mobile }"
          />
        </div>

        <div class="pycad-divider-v" />
      </template>

      <!-- Layout popover -->
      <PycadLayoutPopover />

      <div class="pycad-divider-v" />

      <!-- Load / save -->
      <div class="pycad-topbar__group">
        <PycadToolButton
          icon="mdi-folder-open"
          label="Open files"
          caption="Open"
          show-caption
          @click="onOpen"
        />
        <PycadToolButton
          v-if="showSaveButton"
          icon="mdi-content-save-all"
          label="Save session"
          caption="Save"
          show-caption
          :disabled="!hasData"
          @click="onSave"
        />
      </div>

      <div class="pycad-topbar__sep" />

      <!-- Optional workspace toggles (e.g. implant planning / curved MPR) -->
      <template v-if="enabledWorkspacesList.length">
        <div class="pycad-topbar__group">
          <PycadToolButton
            v-for="ws in enabledWorkspacesList"
            :key="ws.id"
            :icon="ws.icon"
            :label="ws.label"
            :caption="ws.label.split(' ')[0]"
            show-caption
            :shortcut="shortcut(ws.shortcutAction)"
            :active="curvedMPRWorkspaceActive"
            :disabled="!hasData"
            @click="toggleCurvedMPR"
          />
        </div>

        <div class="pycad-topbar__sep" />
      </template>

      <!-- Primary interaction tools -->
      <div class="pycad-topbar__group">
        <PycadToolButton
          icon="mdi-cursor-default"
          label="Select"
          caption="Select"
          show-caption
          :shortcut="shortcut('select')"
          :active="currentTool === Tools.Select"
          @click="setTool(Tools.Select)"
        />
        <PycadToolButton
          icon="mdi-circle-half-full"
          label="Window / Level"
          caption="W/L"
          show-caption
          :shortcut="shortcut('windowLevel')"
          :active="currentTool === Tools.WindowLevel"
          @click="onWindowLevel"
        />
        <PycadToolButton
          icon="mdi-hand-back-right"
          label="Pan"
          caption="Pan"
          show-caption
          :shortcut="shortcut('pan')"
          :active="currentTool === Tools.Pan"
          @click="setTool(Tools.Pan)"
        />
        <PycadToolButton
          icon="mdi-magnify-plus-outline"
          label="Zoom"
          caption="Zoom"
          show-caption
          :shortcut="shortcut('zoom')"
          :active="currentTool === Tools.Zoom"
          @click="setTool(Tools.Zoom)"
        />
      </div>

      <div class="pycad-topbar__sep" v-if="optionalToolbarTools.length" />

      <!-- Measurement / annotation dropdowns -->
      <div v-if="optionalToolbarTools.length" class="pycad-topbar__group">
        <PycadToolDropdown
          v-if="measurementTools.length"
          label="Measure"
          icon="mdi-ruler"
          :items="measurementTools"
          :active-tool="currentTool"
          :disabled="isObliqueLayout"
          :is-item-disabled="(item) => isDisallowedOnCine(item.tool)"
          @select="setTool"
        />
        <PycadToolDropdown
          v-if="annotationTools.length"
          label="Annotate"
          icon="mdi-draw-pen"
          :items="annotationTools"
          :active-tool="currentTool"
          :disabled="isObliqueLayout"
          :is-item-disabled="(item) => isDisallowedOnCine(item.tool)"
          @select="setTool"
        />
        <PycadToolButton
          v-for="toolDef in ungroupedTools"
          :key="toolDef.tool"
          :icon="toolDef.icon"
          :label="toolDef.label"
          :caption="toolDef.label.split(' ')[0]"
          show-caption
          :shortcut="
            toolDef.shortcutAction ? shortcut(toolDef.shortcutAction) : ''
          "
          :active="currentTool === toolDef.tool"
          :disabled="isObliqueLayout || isDisallowedOnCine(toolDef.tool)"
          @click="setTool(toolDef.tool)"
        />
      </div>

      <div class="pycad-topbar__sep" />

      <!-- View orientation + tools/actions (icon-only to save width) -->
      <div class="pycad-topbar__group pycad-topbar__group--view-ops">
        <PycadRotatePopover
          :view-id="activeViewRef ?? undefined"
          :disabled="!canOrientActiveView"
        />
        <PycadToolButton
          icon="mdi-flip-horizontal"
          label="Flip active view horizontally"
          :disabled="!canOrientActiveView"
          @click="onFlipHorizontal"
        />
        <PycadToolButton
          icon="mdi-flip-vertical"
          label="Flip active view vertically"
          :disabled="!canOrientActiveView"
          @click="onFlipVertical"
        />
        <PycadActionDropdown
          label="View"
          icon="mdi-compass-outline"
          compact
          :sections="viewMenuSections"
          :active-item-id="viewMenuActiveId"
          @select="onViewAction"
        />
      </div>
    </div>

    <!-- Always-visible right cluster -->
    <div class="pycad-topbar__pinned">
      <PycadToolButton
        icon="mdi-camera"
        label="Save screenshot of all views"
        :disabled="!hasData"
        @click="onCapture"
      />

      <div class="pycad-divider-v" />

      <!-- Module panel toggles -->
      <div class="pycad-topbar__group">
        <PycadToolButton
          v-for="item in availablePanels"
          :key="item.id"
          :icon="item.icon"
          :label="item.label"
          :icon-size="16"
          :active="isToolbarPanelActive(item.id)"
          @click="togglePanel(item.id)"
        />
      </div>

      <div class="pycad-divider-v" />

      <!-- Coord toggle, notifications, settings, help, connection -->
      <div class="pycad-topbar__right">
        <div v-if="showCoordinateSystemToggle" class="pycad-coord-toggle">
          <button
            type="button"
            class="pycad-coord-toggle__btn"
            :class="{
              'pycad-coord-toggle__btn--active': coordinateSystem === 'LPS',
            }"
            @click="setCoordinateSystem('LPS')"
          >
            LPS
          </button>
          <button
            type="button"
            class="pycad-coord-toggle__btn"
            :class="{
              'pycad-coord-toggle__btn--active': coordinateSystem === 'RAS',
            }"
            @click="setCoordinateSystem('RAS')"
          >
            RAS
          </button>
        </div>

        <v-badge
          offset-x="10"
          offset-y="10"
          :content="msgCount"
          :color="msgBadgeColor"
          :model-value="msgCount > 0"
        >
          <PycadToolButton
            icon="mdi-bell-outline"
            label="Notifications"
            @click="messageDialog = true"
          />
        </v-badge>
        <PycadToolButton
          icon="mdi-cog"
          label="Settings"
          @click="settingsDialog = true"
        />
        <PycadToolButton
          v-if="showAboutButton"
          icon="mdi-information-outline"
          label="About SmileSuite"
          @click="aboutDialog = true"
        />

        <div class="pycad-conn">
          <span
            class="pycad-conn__dot"
            :class="connClass"
            :style="{ background: connColor }"
          ></span>
          <span class="pycad-conn__text" :style="{ color: connColor }">
            {{ connLabel }}
          </span>
        </div>
      </div>
    </div>

    <!-- Dialogs hosted by the toolbar -->
    <CloseableDialog v-model="settingsDialog">
      <Settings />
    </CloseableDialog>

    <CloseableDialog v-model="saveDialog" max-width="30%">
      <template v-slot="{ close }">
        <SaveSession :close="close" />
      </template>
    </CloseableDialog>

    <CloseableDialog v-model="messageDialog" content-class="fill-height">
      <MessageCenter />
    </CloseableDialog>

    <CloseableDialog
      v-model="aboutDialog"
      width="860px"
      max-width="860px"
      :close-offset-x="14"
      :close-offset-y="14"
    >
      <AboutBox />
    </CloseableDialog>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useDisplay } from 'vuetify';

import { Tools } from '@/src/store/tools/types';
import { useToolStore, isToolAllowedFor } from '@/src/store/tools';
import { useViewStore } from '@/src/store/views';
import { useEffectiveView } from '@/src/composables/useEffectiveView';
import { actionToKey } from '@/src/composables/useKeyboardShortcuts';
import { useImageStore } from '@/src/store/datasets-images';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import { ConnectionState, useServerStore } from '@/src/store/server';
import { MessageType, useMessageStore } from '@/src/store/messages';
import useRemoteSaveStateStore from '@/src/store/remote-save-state';
import { usePycadUiStore, type PycadPanelId } from '@/src/store/pycad-ui';
import { useModelStore } from '@/src/store/datasets-models';
import { loadUserPromptedFiles } from '@/src/actions/loadUserFiles';
import { triggerResetViews } from '@/src/components/tools/ResetViews.vue';
import { triggerCameraOp } from '@/src/core/cameraOps';
import {
  enabledToolbarPanels,
  enabledToolbarTools,
  enabledWorkspaces,
  getAutoOpenPanelForTool,
} from '@/src/features/registry';
import { isFeatureCompiled } from '@/src/features/featureFlags';

import PycadToolButton from '@/src/components/pycad/PycadToolButton.vue';
import PycadToolDropdown from '@/src/components/pycad/PycadToolDropdown.vue';
import PycadActionDropdown from '@/src/components/pycad/PycadActionDropdown.vue';
import PycadRotatePopover from '@/src/components/pycad/PycadRotatePopover.vue';
import PycadLayoutPopover from '@/src/components/pycad/PycadLayoutPopover.vue';
import smileSuiteLogo from '@/src/assets/smilesuite_logo.svg';
import CloseableDialog from '@/src/components/CloseableDialog.vue';
import Settings from '@/src/components/Settings.vue';
import SaveSession from '@/src/components/SaveSession.vue';
import MessageCenter from '@/src/components/MessageCenter.vue';
import AboutBox from '@/src/components/AboutBox.vue';

const { mobile } = useDisplay();

// True when running inside an iframe (embedded host page).
const isEmbedded = window.self !== window.top;
// Temporarily hidden from the toolbar; underlying actions/state stay intact.
const showSaveButton = false;
const showAboutButton = false;
const showCoordinateSystemToggle = false;

// --- Tool store --- //
const toolStore = useToolStore();
const { currentTool } = storeToRefs(toolStore);

function setTool(tool: Tools) {
  // Toggle behavior matches pycad: click an active tool to deselect, but
  // VolView prefers reverting to Select rather than `undefined`.
  if (currentTool.value === tool) {
    toolStore.setCurrentTool(Tools.Select);
  } else {
    toolStore.setCurrentTool(tool);
  }
}

// --- View gating --- //
// Tools are kept clickable in the empty state; we only gate on view-kind
// (oblique / cine) since some tools genuinely don't apply to those view
// types and would error out at runtime.
const viewStore = useViewStore();
const { activeView: activeViewRef, currentLayoutName } = storeToRefs(viewStore);
const activeEffective = useEffectiveView(
  computed(() => activeViewRef.value ?? '')
);
const isObliqueLayout = computed(
  () => activeEffective.value?.kind === 'oblique'
);
const isCineActive = computed(() => activeEffective.value?.kind === 'cine');
const isDisallowedOnCine = (tool: Tools) =>
  isCineActive.value && !isToolAllowedFor(tool, activeEffective.value);

// --- Has-data --- //
const imageStore = useImageStore();
const dicomStore = useDICOMStore();
const hasData = computed(
  () =>
    imageStore.idList.length > 0 ||
    Object.keys(dicomStore.volumeInfo).length > 0
);

// --- Keyboard hints --- //
function shortcut(action: keyof typeof actionToKey.value): string {
  const k = actionToKey.value[action];
  if (!k) return '';
  return k.length === 1 ? k.toUpperCase() : k;
}

// --- Right panel toggles --- //
const ui = usePycadUiStore();
const {
  activePanel,
  coordinateSystem,
  curvedMPRWorkspaceActive,
  panoramicSecondaryPanel,
  panoramicInspectorTab,
} = storeToRefs(ui);

function toggleCurvedMPR() {
  ui.toggleCurvedMPRWorkspace();
}

const serverStore = useServerStore();
const { url: serverUrl, connState } = storeToRefs(serverStore);

const optionalToolbarTools = enabledToolbarTools();
const measurementTools = optionalToolbarTools.filter(
  (t) => t.category === 'measurement'
);
const annotationTools = optionalToolbarTools.filter(
  (t) => t.category === 'annotation'
);
const ungroupedTools = optionalToolbarTools.filter((t) => !t.category);
const enabledWorkspacesList = enabledWorkspaces();

const availablePanels = computed(() =>
  enabledToolbarPanels().filter(
    (p) => p.id !== 'windowlevel' && (p.id !== 'remote' || !!serverUrl.value)
  )
);

function isToolbarPanelActive(id: PycadPanelId) {
  if (curvedMPRWorkspaceActive.value) {
    return (
      panoramicSecondaryPanel.value === id &&
      panoramicInspectorTab.value === 'utility'
    );
  }
  return activePanel.value === id;
}

function onWindowLevel() {
  if (curvedMPRWorkspaceActive.value) {
    if (currentTool.value === Tools.WindowLevel) {
      toolStore.setCurrentTool(Tools.Select);
    } else {
      toolStore.setCurrentTool(Tools.WindowLevel);
    }
    return;
  }

  const toolActive = currentTool.value === Tools.WindowLevel;
  const panelOpen = activePanel.value === 'windowlevel';

  if (toolActive && panelOpen) {
    toolStore.setCurrentTool(Tools.Select);
    ui.closePanel();
  } else {
    toolStore.setCurrentTool(Tools.WindowLevel);
    ui.openPanel('windowlevel');
  }
}

function togglePanel(id: PycadPanelId) {
  if (curvedMPRWorkspaceActive.value) {
    ui.togglePanoramicUtilityPanel(id);
    return;
  }

  if (activePanel.value === id) {
    ui.closePanel();
  } else {
    ui.openPanel(id);
  }
}

function setCoordinateSystem(s: 'LPS' | 'RAS') {
  ui.setCoordinateSystem(s);
}

// Auto-open the relevant panel when the user activates an optional tool.
watch(currentTool, (newTool) => {
  if (curvedMPRWorkspaceActive.value) {
    const panelId = getAutoOpenPanelForTool(newTool);
    if (panelId) {
      ui.openPanoramicUtilityPanel(panelId);
    }
    return;
  }

  if (newTool === Tools.WindowLevel) {
    ui.openPanel('windowlevel');
  }

  const panelId = getAutoOpenPanelForTool(newTool);
  if (panelId) {
    ui.openPanel(panelId);
  }
});

const modelStore = useModelStore();
watch(
  () => modelStore.idList.length,
  (count, prev) => {
    if (count > prev) {
      if (curvedMPRWorkspaceActive.value) {
        ui.openPanoramicUtilityPanel('meshes');
      } else {
        ui.openPanel('meshes');
      }
    }
  }
);

// --- Dialogs --- //
const settingsDialog = ref(false);
const saveDialog = ref(false);
const messageDialog = ref(false);
const aboutDialog = ref(false);

// --- Save (handle remote save shortcut when configured) --- //
const remoteSaveStore = useRemoteSaveStateStore();
const { saveUrl } = storeToRefs(remoteSaveStore);
function onSave() {
  if (!hasData.value) return;
  if (saveUrl.value !== '') {
    remoteSaveStore.saveState();
  } else {
    saveDialog.value = true;
  }
}

function onOpen() {
  loadUserPromptedFiles();
}

function onResetViews() {
  triggerResetViews();
}

// --- Rotate / flip active view --- //
// Only meaningful on 2D slice-like views (slice or cine).
const canOrientActiveView = computed(() => {
  if (!hasData.value) return false;
  const kind = activeEffective.value?.kind;
  return kind === 'volume2D' || kind === 'cine';
});

function onFlipHorizontal() {
  const viewId = activeViewRef.value;
  if (!viewId || !canOrientActiveView.value) return;
  triggerCameraOp({ viewId, op: 'flipHorizontal' });
}

function onFlipVertical() {
  const viewId = activeViewRef.value;
  if (!viewId || !canOrientActiveView.value) return;
  triggerCameraOp({ viewId, op: 'flipVertical' });
}

const viewMenuActiveId = computed(() => {
  if (currentTool.value === Tools.Crosshairs) return 'crosshairs';
  if (currentTool.value === Tools.Crop) return 'crop';
  if (currentLayoutName.value === 'Oblique') return 'oblique';
  return '';
});

const viewMenuSections = computed(() => {
  const toolItems = [
    {
      id: 'crosshairs',
      icon: 'mdi-crosshairs-gps',
      label: 'Crosshairs',
      shortcut: shortcut('crosshairs'),
      disabled: isObliqueLayout.value || isDisallowedOnCine(Tools.Crosshairs),
    },
    ...(isFeatureCompiled('cropping')
      ? [
          {
            id: 'crop',
            icon: 'mdi-crop',
            label: 'Crop volume',
            shortcut: shortcut('crop'),
            disabled: isObliqueLayout.value || isDisallowedOnCine(Tools.Crop),
          },
        ]
      : []),
  ];

  return [
    { title: 'Tools', items: toolItems },
    {
      title: 'Actions',
      items: [
        {
          id: 'oblique',
          icon: 'mdi-axis-arrow',
          label: 'Oblique views',
          disabled: !hasData.value,
        },
        {
          id: 'reset',
          icon: 'mdi-refresh',
          label: 'Reset views',
        },
      ],
    },
  ];
});

function onViewAction(id: string) {
  switch (id) {
    case 'crosshairs':
      setTool(Tools.Crosshairs);
      break;
    case 'crop':
      setTool(Tools.Crop);
      break;
    case 'oblique':
      viewStore.switchToNamedLayout('Oblique');
      break;
    case 'reset':
      onResetViews();
      break;
    default:
      break;
  }
}

// --- Screenshot --- //
async function onCapture() {
  try {
    const { captureViewportScreenshot } = await import('@/src/core/screenshot');
    await captureViewportScreenshot();
  } catch (err) {
    useMessageStore().addError('Failed to capture screenshot', {
      error: err instanceof Error ? err : new Error(String(err)),
    });
  }
}

// --- Notifications badge --- //
const messageStore = useMessageStore();
const msgCount = computed(() => messageStore.importantMessages.length);
const msgBadgeColor = computed(() => {
  if (
    messageStore.importantMessages.find((m) => m.type === MessageType.Error)
  ) {
    return 'error';
  }
  if (
    messageStore.importantMessages.find((m) => m.type === MessageType.Warning)
  ) {
    return 'warning';
  }
  return 'primary';
});

// --- Connection indicator --- //
const connColor = computed(() => {
  if (!serverUrl.value) return '#666666';
  switch (connState.value) {
    case ConnectionState.Connected:
      return '#3B82F6';
    case ConnectionState.Pending:
      return '#ff9900';
    case ConnectionState.Disconnected:
    default:
      return '#FF5C5C';
  }
});

const connLabel = computed(() => {
  if (!serverUrl.value) return 'Local';
  switch (connState.value) {
    case ConnectionState.Connected:
      return 'Live';
    case ConnectionState.Pending:
      return 'Connecting';
    case ConnectionState.Disconnected:
    default:
      return 'Offline';
  }
});

const connClass = computed(() => ({
  'pycad-pulse': connState.value === ConnectionState.Connected,
}));
</script>

<style scoped>
.pycad-topbar {
  display: flex;
  align-items: center;
  height: var(--pycad-topbar-height, 58px);
  padding: 0 14px;
  gap: 10px;
  background: var(--pycad-surface);
  border-bottom: 1px solid var(--pycad-border);
  flex-shrink: 0;
  overflow: visible;
  font-family: 'Inter', system-ui, sans-serif;
  z-index: 100;
}

.pycad-topbar__scroll {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 0;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
}

.pycad-topbar__scroll::-webkit-scrollbar {
  display: none;
}

.pycad-topbar__group--view-ops :deep(.pycad-rotate-popover__trigger) {
  flex-direction: row;
  width: 34px;
  min-width: 34px;
  height: 34px;
  padding: 0;
  justify-content: center;
}

.pycad-topbar__group--view-ops :deep(.pycad-rotate-popover__caption) {
  display: none;
}

.pycad-topbar__pinned {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  margin-left: auto;
  position: relative;
  z-index: 2;
  background: var(--pycad-surface);
  padding-left: 4px;
}

.pycad-topbar__brand {
  display: flex;
  align-items: center;
  margin-right: 4px;
  color: var(--pycad-text-primary);
}

.pycad-topbar__logo {
  height: 32px;
  width: auto;
  max-width: 200px;
  display: block;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

.pycad-topbar__logo--mobile {
  height: 26px;
  max-width: 160px;
}

.pycad-topbar__group {
  display: flex;
  align-items: center;
  gap: 3px;
}

.pycad-topbar__sep {
  width: 1px;
  height: 30px;
  background: var(--pycad-border);
  margin: 0 4px;
  flex-shrink: 0;
}

.pycad-topbar__right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pycad-coord-toggle {
  display: flex;
  border-radius: var(--pycad-radius-sm, 6px);
  border: 1px solid var(--pycad-border);
  overflow: hidden;
}

.pycad-coord-toggle__btn {
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: none;
  outline: none;
  font-family: 'JetBrains Mono', monospace;
  background: var(--pycad-surface);
  color: var(--pycad-text-muted);
}

.pycad-coord-toggle__btn:hover {
  color: var(--pycad-text-primary);
}

.pycad-coord-toggle__btn--active {
  background: var(--pycad-primary);
  color: #050505;
}

.pycad-conn {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 4px;
  font-family: 'Inter', sans-serif;
}

.pycad-conn__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.pycad-conn__text {
  font-size: 12px;
}

@media (max-width: 1480px) {
  .pycad-topbar__scroll :deep(.pycad-tool-btn__caption),
  .pycad-topbar__scroll :deep(.pycad-layout-trigger__label),
  .pycad-topbar__scroll :deep(.pycad-layout-trigger__chev) {
    display: none;
  }

  .pycad-topbar__scroll :deep(.pycad-tool-btn--labeled),
  .pycad-topbar__scroll :deep(.pycad-layout-trigger) {
    width: 34px;
    min-width: 34px;
    height: 34px;
    padding: 0;
    flex-direction: row;
    justify-content: center;
  }
}

@media (max-width: 1180px) {
  .pycad-conn__text {
    display: none;
  }
}
</style>
